"use client";

import {JSX} from "react";

import {useModel} from "@/shared/hooks";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {HtmlContent} from "@/shared/ui/data-display/html-content";
import {Typography} from "@/shared/ui/data-display/typography";
import {TextField} from "@/shared/ui/form/fields/text-field";

import {FaqsSearchModel, type FaqsSearchModelInstance} from "./faqs-search.model";
import type {ContentApiFaqGroup, ContentApiFaqsSearchSection} from "./type";

import "./styles/faqs-search.scss";

interface FaqGroupProps {
    group: ContentApiFaqGroup;
    visibleFaqs: Array<{id: string; question: string; answer: string}>;
    showMore: boolean;
    isItemOpen: (id: string) => boolean;
    onToggleItem: (id: string) => void;
    onToggleGroup: () => void;
}

function FaqGroup({group, visibleFaqs, showMore, isItemOpen, onToggleItem, onToggleGroup}: FaqGroupProps): JSX.Element {
    return (
        <div className="faqs-search__group">
            <Typography as="h3" size={{desktop: 24, mobile: 18}} weight={600} className="faqs-search__group-title">
                {group.title}
            </Typography>

            <div className="faqs-search__list">
                {visibleFaqs.map((faq) => {
                    const isOpen = isItemOpen(faq.id);
                    return (
                        <div key={faq.id} className={`faqs-search__item ${isOpen ? "faqs-search__item--open" : ""}`}>
                            <button type="button" className="faqs-search__question" onClick={() => onToggleItem(faq.id)} aria-expanded={isOpen}>
                                <Typography as="span" size={{desktop: 16, mobile: 14}} weight={500} innerHTML={faq.question} />
                                <span className="faqs-search__icon">
                                    <span className="faqs-search__icon-line faqs-search__icon-line--horizontal" />
                                    <span className="faqs-search__icon-line faqs-search__icon-line--vertical" />
                                </span>
                            </button>

                            <div className="faqs-search__answer">
                                <HtmlContent html={faq.answer} className="faqs-search__answer-content" />
                            </div>
                        </div>
                    );
                })}
            </div>

            {showMore && (
                <div className="faqs-search__show-more">
                    <ButtonBase variant="primary" size="sm" width="auto" onClick={onToggleGroup}>
                        Show More
                    </ButtonBase>
                </div>
            )}
        </div>
    );
}

interface FaqsSearchViewProps {
    data: ContentApiFaqsSearchSection;
}

export function FaqsSearchView({data}: FaqsSearchViewProps): JSX.Element {
    const [model, snapshot] = useModel<FaqsSearchModelInstance, [Array<ContentApiFaqGroup>]>(FaqsSearchModel, data.groups || []);
    const {t} = useTranslation("common");

    const groups = (data.groups || []).map((group) => ({
        group,
        visibleFaqs: snapshot.getVisibleFaqs(group),
        showMore: snapshot.hasMore(group),
    }));
    const hasAnyResults = groups.some((g) => g.visibleFaqs.length > 0);

    return (
        <div className="faqs-search">
            <div className="faqs-search__search">
                <TextField model={model.searchField} label="Search" placeholder="Search FAQs..." type="search" height="small" />
            </div>

            {hasAnyResults ? (
                <div className="faqs-search__groups">
                    {groups.map(({group, visibleFaqs, showMore}) => {
                        if (visibleFaqs.length === 0) return null;

                        return (
                            <FaqGroup
                                key={group.id}
                                group={group}
                                visibleFaqs={visibleFaqs}
                                showMore={showMore}
                                isItemOpen={(id: string) => snapshot.isItemOpen(id)}
                                onToggleItem={(id: string) => model.toggleItem(id)}
                                onToggleGroup={() => model.toggleGroup(group.id)}
                            />
                        );
                    })}
                </div>
            ) : (
                <Typography as="p" size={{desktop: 18, mobile: 14}} weight={500} className="faqs-search__no-results">
                    {t("noResultsFound")}
                </Typography>
            )}
        </div>
    );
}
